/**
 * Hardened Cloudinary URL builder - single source of truth for video URLs
 */

const CLOUD_NAME = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
const BASE_URL = `https://res.cloudinary.com/${CLOUD_NAME}`;

export type CloudinaryUrls = {
  publicId: string;        // normalized "folder/name" without extension
  mp4Url: string;          // playable mp4
  posterUrl: string;       // jpg derived from the video
};

/**
 * Build Cloudinary video and poster URLs from raw input
 * Handles full URLs, public_ids with/without extensions, and normalizes everything
 * @param rawIdOrUrl - Raw Cloudinary URL, public_id, or public_id with extension
 * @returns Normalized URLs or null if invalid input
 */
export function buildCloudinaryVideoUrls(rawIdOrUrl?: string): CloudinaryUrls | null {
  if (!CLOUD_NAME || !rawIdOrUrl) return null;

  // 1) Normalize any input (full URL, public_id with/without ext)
  let pid = rawIdOrUrl.trim();

  // If a full Cloudinary URL was passed, extract its public_id
  // e.g. https://res.cloudinary.com/<cloud>/video/upload/.../folder/name.mp4
  try {
    if (pid.startsWith('http')) {
      const u = new URL(pid);
      const parts = u.pathname.split('/'); // ['', '<cloud>', 'video|image', 'upload', ...folders, 'name.ext']
      const idx = parts.findIndex(p => p === 'upload');
      if (idx >= 0 && idx + 1 < parts.length) {
        pid = parts.slice(idx + 1).join('/'); // folder/name.ext
      }
    }
  } catch {/* ignore malformed URLs */}

  // Strip query/hash
  pid = pid.replace(/[?#].*$/, '');

  // 2) Drop extension if present (Cloudinary wants bare public_id)
  pid = pid.replace(/\.(mp4|webm|mov|mkv|jpg|jpeg|png|gif|svg)$/i, '');

  // 3) Safety: decode and collapse spaces
  pid = decodeURIComponent(pid).replace(/\s+/g, '_');

  // Validate we have something left
  if (!pid || pid.length < 2) return null;

  // 4) Build URLs
  // NOTE: path MUST start with /video/upload for video resources
  // - f_mp4 ensures mp4 output
  // - q_auto:eco keeps size small
  // - ac_none avoids audio codec surprises for muted autoplay
  // - vc_h264 maximizes compatibility
  const base = `https://res.cloudinary.com/${CLOUD_NAME}`;
  const mp4Url = `${base}/video/upload/f_mp4,q_auto:eco,ac_none,vc_h264/${pid}.mp4`;
  // Poster from video frame zero (so_0) as jpg
  const posterUrl = `${base}/video/upload/so_0,q_70/${pid}.jpg`;

  return { publicId: pid, mp4Url, posterUrl };
}

/**
 * Detect whether a Cloudinary resource is a video or image
 * @param pid - Public ID without extension
 * @returns 'video' | 'image' | 'video' (default)
 */
async function pickResourceType(pid: string): Promise<'video' | 'image'> {
  // Try video first
  const videoCheck = await safeHead(`${BASE_URL}/video/upload/${pid}.mp4`, 1500);
  if (videoCheck) return 'video';
  
  // Try image
  const imageCheck = await safeHead(`${BASE_URL}/image/upload/${pid}.jpg`, 1500);
  if (imageCheck) return 'image';
  
  // Default to video (might be a transcoding issue or timing)
  return 'video';
}

/**
 * Build Cloudinary poster URL with resource type detection
 * @param pid - Public ID without extension
 * @returns Poster URL (.jpg)
 */
export async function buildPosterUrl(pid: string): Promise<string> {
  const type = await pickResourceType(pid);
  return type === 'video'
    ? `${BASE_URL}/video/upload/so_0,q_70/${pid}.jpg`
    : `${BASE_URL}/image/upload/q_70/${pid}.jpg`;
}

/**
 * Build Cloudinary MP4 URL (only for videos)
 * @param pid - Public ID without extension
 * @returns MP4 URL or null if not a video
 */
export async function buildMp4Url(pid: string): Promise<string | null> {
  const type = await pickResourceType(pid);
  if (type !== 'video') return null;
  return `${BASE_URL}/video/upload/f_mp4,q_auto:eco,ac_none,vc_h264/${pid}.mp4`;
}

/**
 * Preflight HEAD request to check if a URL is accessible
 * @param url - URL to check
 * @param ms - Timeout in milliseconds
 * @returns true if URL returns 200-299, false otherwise
 */
export async function safeHead(url: string, ms = 2500): Promise<boolean> {
  try {
    const ctl = new AbortController();
    const tid = setTimeout(() => ctl.abort(), ms);
    const res = await fetch(url, { method: 'HEAD', mode: 'cors', signal: ctl.signal });
    clearTimeout(tid);
    return res.ok;
  } catch {
    return false;
  }
}
